import { gql, useQuery } from '@apollo/client'

/**
 * The connected Instagram account, as the CMS sees it: which app, which
 * account, until when — never the app secret or the token, which only the
 * feed service can unseal. Writes go through the instagram_* actions
 * (nautix/lib/instagram-job.mjs).
 */
export const INSTAGRAM = gql`
  query Instagram {
    account: instagram_account_by_pk(id: 1) {
      app_id
      username
      connected_at
      token_expires_at
      last_error
    }
    posts: instagram_posts(order_by: { created_at: desc }, limit: 5) {
      id
      created_at
      kind
      ok
      message
      permalink
      boat {
        id
        name
      }
    }
  }
`

export const SAVE_APP = gql`
  mutation SaveInstagramApp($app_id: String!, $app_secret: String!) {
    instagram_save_app(app_id: $app_id, app_secret: $app_secret) {
      saved
      app_id
    }
  }
`

export const CONNECT = gql`
  mutation InstagramConnect($code: String!, $redirect_uri: String!) {
    instagram_connect(code: $code, redirect_uri: $redirect_uri) {
      username
    }
  }
`

export const DISCONNECT = gql`
  mutation InstagramDisconnect {
    instagram_disconnect {
      ok
    }
  }
`

export const PUBLISH = gql`
  mutation InstagramPublish(
    $kind: String!
    $file_ids: [String!]!
    $caption: String
    $boat_id: Int
  ) {
    instagram_publish(kind: $kind, file_ids: $file_ids, caption: $caption, boat_id: $boat_id) {
      ok
      message
      permalink
      post_id
    }
  }
`

export interface InstagramAccount {
  app_id: string
  username: string | null
  connected_at: string | null
  token_expires_at: string | null
  last_error: string | null
}

export interface InstagramPost {
  id: number
  created_at: string
  kind: 'feed' | 'story'
  ok: boolean
  message: string | null
  permalink: string | null
  boat: { id: number; name: string | null } | null
}

export const useInstagram = () => {
  const { data, refetch } = useQuery<{
    account: InstagramAccount | null
    posts: InstagramPost[]
  }>(INSTAGRAM, { fetchPolicy: 'cache-and-network' })
  const account = data?.account ?? null
  const expired = !!account?.token_expires_at && Date.parse(account.token_expires_at) < Date.now()
  return {
    loaded: !!data,
    account,
    posts: data?.posts ?? [],
    /** The account name when publishing is possible right now. */
    connectedAs: account?.username && !expired ? account.username : null,
    expired,
    refetch,
  }
}

/** Where Instagram sends the person back; registered as is in the Meta app. */
export const redirectUri = () => `${window.location.origin}/instagram`

const STATE_KEY = 'instagram.oauth.state'

/** Instagram's login page for this app, with a one-time state to check on return. */
export const loginUrl = (appId: string) => {
  const state = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('')
  try {
    sessionStorage.setItem(STATE_KEY, state)
  } catch {
    // Without storage the state cannot be checked; the return is refused.
  }
  const url = new URL('https://www.instagram.com/oauth/authorize')
  url.searchParams.set('client_id', appId)
  url.searchParams.set('redirect_uri', redirectUri())
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('scope', 'instagram_business_basic,instagram_business_content_publish')
  url.searchParams.set('state', state)
  return url.toString()
}

/** The state the login started with — once; a second look finds nothing. */
export const takeState = () => {
  try {
    const state = sessionStorage.getItem(STATE_KEY)
    sessionStorage.removeItem(STATE_KEY)
    return state
  } catch {
    return null
  }
}
